import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import {
  getPackage, getQuotes, getPackageBidLeveling,
  getPackageAwardRecommendation, generateAwardRecommendation,
  approveAward, transitionPackage
} from '../utils/api';
import LifecyclePill from '../components/LifecyclePill';
import AuditTrail from '../components/AuditTrail';

const fmt = n => n ? '£' + Number(n).toLocaleString() : '—';

export default function AwardMemo() {
  const { packageId } = useParams();
  const { selectedProject, addToast } = useOutletContext();
  const navigate = useNavigate();

  const [pkg, setPkg] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [bidLeveling, setBidLeveling] = useState(null);
  const [awardRec, setAwardRec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [approving, setApproving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => { if (packageId) loadAll(); }, [packageId]);

  async function loadAll() {
    setLoading(true);
    try {
      const [pkgData, quotesData, blData, arData] = await Promise.all([
        getPackage(packageId),
        getQuotes(packageId).catch(() => []),
        getPackageBidLeveling(packageId).catch(() => null),
        getPackageAwardRecommendation(packageId).catch(() => null),
      ]);
      setPkg(pkgData);
      setQuotes(quotesData || []);
      setBidLeveling(blData);
      setAwardRec(arData);
    } catch (err) { console.error(err); }
    setLoading(false);
  }

  async function handleGenerate() {
    setGenerating(true);
    try {
      const result = await generateAwardRecommendation(packageId);
      setAwardRec(result);
      setMessage({ type: 'success', text: '✦ Award memo generated by AI' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
    setGenerating(false);
  }

  async function handleApprove() {
    setApproving(true);
    try {
      await approveAward(packageId, awardRec.id, '');
      await transitionPackage(packageId, 'award_approved');
      setMessage({ type: 'success', text: '✓ Award approved — package moving to Contract/PO' });
      await loadAll();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
    setApproving(false);
  }

  async function handleReject() {
    try {
      await transitionPackage(packageId, 'bid_leveling');
      setMessage({ type: 'error', text: 'Award rejected — returned to bid leveling' });
      await loadAll();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  }

  if (loading) return <div className="page"><div className="empty-state"><div className="spinner" /></div></div>;
  if (!pkg) return <div className="page"><div className="empty-state"><h3>Package not found</h3><button onClick={() => navigate('/packages')} className="btn btn-primary">← Back</button></div></div>;

  const memo = awardRec?.memo || {};
  const isApproved = awardRec?.human_approval_status === 'approved';
  const state = pkg.lifecycle_state || 'draft_scope';

  return (
    <div className="page">

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <button onClick={() => navigate(`/packages/${packageId}`)} className="btn btn-ghost btn-sm">← Package</button>
            <span style={{ color: 'var(--color-border-mid)' }}>›</span>
            <span style={{ fontSize: '0.875rem', color: 'var(--color-text-mid)' }}>Award Memo</span>
          </div>
          <h1 style={{ fontSize: '1.375rem', fontWeight: 700, marginBottom: 6 }}>{pkg.name}</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <LifecyclePill state={state} size="lg" />
            <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-mid)' }}>{pkg.trade_category}</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          {awardRec && !isApproved && (
            <>
              <button onClick={handleReject} className="btn btn-danger btn-sm">✕ Reject</button>
              <button onClick={handleApprove} disabled={approving} className="btn btn-approve">
                {approving ? '...' : '✓ Approve Award'}
              </button>
            </>
          )}
          {!awardRec && (
            <button onClick={handleGenerate} disabled={generating} className="btn btn-primary">
              {generating ? '✦ AI Generating...' : '✦ Generate Award Memo'}
            </button>
          )}
          {awardRec && (
            <button onClick={handleGenerate} disabled={generating} className="btn btn-secondary btn-sm">
              {generating ? '...' : '↺ Regenerate'}
            </button>
          )}
        </div>
      </div>

      {message && (
        <div style={{ padding: '12px 16px', borderRadius: 'var(--radius-md)', fontSize: '0.875rem', fontWeight: 500, marginBottom: 16, background: message.type === 'success' ? '#ECFDF5' : '#FEF2F2', color: message.type === 'success' ? 'var(--color-success)' : 'var(--color-error)', border: `1px solid ${message.type === 'success' ? 'var(--color-success)' : 'var(--color-error)'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {message.text}
          <button onClick={() => setMessage(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', opacity: 0.5, fontSize: '1rem' }}>✕</button>
        </div>
      )}

      {!awardRec ? (
        <div className="card">
          <div className="card-body" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: 60, textAlign: 'center' }}>
            <span className="ai-badge" style={{ fontSize: '0.875rem', padding: '6px 16px' }}>✦ AI Award Memo</span>
            <h2>Generate Award Recommendation Memo</h2>
            <p style={{ maxWidth: 480, lineHeight: 1.7 }}>
              AI will analyze all quotes received, the bid leveling results, and prepare a formal award recommendation memo including price analysis, risk assessment, commercial summary, and next steps.
            </p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
              <div style={{ padding: '10px 16px', background: 'var(--color-background)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', fontSize: '0.875rem', color: 'var(--color-text-mid)' }}>
                {quotes.length} quote{quotes.length !== 1 ? 's' : ''} received
              </div>
              <div style={{ padding: '10px 16px', background: 'var(--color-background)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', fontSize: '0.875rem', color: 'var(--color-text-mid)' }}>
                {bidLeveling ? 'Bid leveling complete' : 'No bid leveling yet'}
              </div>
            </div>
            <button onClick={handleGenerate} disabled={generating || quotes.length === 0} className="btn btn-primary" style={{ padding: '12px 32px', fontSize: '1rem' }}>
              {generating ? '✦ AI is writing memo...' : quotes.length === 0 ? 'Add quotes first' : '✦ Generate Award Memo'}
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 16 }}>

          {/* Main memo */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Approval status banner */}
            <div style={{
              padding: '16px 20px',
              borderRadius: 'var(--radius-lg)',
              border: `2px solid ${isApproved ? 'var(--color-success)' : 'var(--color-warning)'}`,
              background: isApproved ? '#ECFDF5' : '#FFFBEB',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span className="ai-badge">✦ AI Prepared</span>
                  <span style={{ fontSize: '0.875rem', fontWeight: 700, color: isApproved ? 'var(--color-success)' : 'var(--color-warning)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {isApproved ? '✓ Award Approved' : '⏳ Pending Human Approval'}
                  </span>
                </div>
                {!isApproved && (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={handleReject} className="btn btn-danger btn-sm">✕ Reject</button>
                    <button onClick={handleApprove} disabled={approving} className="btn btn-approve">
                      {approving ? '...' : '✓ Approve Award'}
                    </button>
                  </div>
                )}
                {isApproved && (
                  <div style={{ fontSize: '0.875rem', color: 'var(--color-success)', fontWeight: 500 }}>
                    Approved by procurement team
                  </div>
                )}
              </div>
              {!isApproved && (
                <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-dark)', marginTop: 10, lineHeight: 1.6 }}>
                  AI has prepared this award recommendation. Review all sections carefully before approving. Once approved, the package will move to Contract/PO stage.
                </p>
              )}
            </div>

            {/* Memo header */}
            <div className="card">
              <div style={{ padding: '24px 28px', borderBottom: '1px solid var(--color-border)', background: 'var(--color-navy)', borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0' }}>
                <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Award Recommendation Memo</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#fff', marginBottom: 4 }}>{pkg.name}</div>
                <div style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.6)' }}>{pkg.trade_category} · {selectedProject?.name}</div>
              </div>
              <div className="card-body">
                {/* Recommended supplier highlight */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 20, padding: '20px', background: isApproved ? '#ECFDF5' : 'var(--color-teal-light)', borderRadius: 'var(--radius-md)', border: `1px solid ${isApproved ? 'var(--color-success)' : 'var(--color-teal)'}`, marginBottom: 20 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: isApproved ? 'var(--color-success)' : 'var(--color-teal)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                      {isApproved ? '✓ Awarded To' : 'Recommended Supplier'}
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-text-dark)' }}>
                      {memo.recommended_supplier || awardRec.recommended_supplier_name}
                    </div>
                    {memo.recommendation_summary && (
                      <div style={{ fontSize: '0.875rem', color: 'var(--color-text-mid)', marginTop: 6, lineHeight: 1.5 }}>
                        {memo.recommendation_summary}
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-light)', marginBottom: 4 }}>Recommended Price</div>
                    <div style={{ fontSize: '2rem', fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--color-text-dark)', letterSpacing: '-0.03em' }}>
                      {fmt(memo.recommended_price)}
                    </div>
                  </div>
                </div>

                {/* Executive Summary */}
                {memo.executive_summary && (
                  <MemoSection title="Executive Summary" icon="◎">
                    <p style={{ lineHeight: 1.8 }}>{memo.executive_summary}</p>
                  </MemoSection>
                )}
              </div>
            </div>

            {/* Price Analysis */}
            {(memo.price_analysis || quotes.length > 0) && (
              <div className="card">
                <div className="card-header"><span style={{ fontWeight: 600 }}>Price Analysis</span></div>
                <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {quotes.length > 0 && (
                    <div className="table-wrapper">
                      <table>
                        <thead>
                          <tr>{['Supplier', 'Total Price', 'Lead Time', 'Payment Terms', 'Exclusions'].map(h => <th key={h}>{h}</th>)}</tr>
                        </thead>
                        <tbody>
                          {[...quotes].sort((a, b) => (a.total_price || Infinity) - (b.total_price || Infinity)).map((q, i) => {
                            const isRec = q.supplier_name === (memo.recommended_supplier || awardRec.recommended_supplier_name);
                            return (
                              <tr key={q.id} style={{ background: isRec ? '#F0FDF4' : 'transparent' }}>
                                <td style={{ fontWeight: 600 }}>
                                  {q.supplier_name}
                                  {isRec && <span className="badge badge-success" style={{ marginLeft: 8, fontSize: '0.6875rem' }}>Recommended</span>}
                                </td>
                                <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: isRec ? 'var(--color-success)' : 'var(--color-text-dark)' }}>{fmt(q.total_price)}</td>
                                <td style={{ fontFamily: 'var(--font-mono)' }}>{q.lead_time_days ? `${q.lead_time_days}d` : '—'}</td>
                                <td style={{ fontSize: '0.8125rem' }}>{q.payment_terms || '—'}</td>
                                <td style={{ fontSize: '0.8125rem', color: q.exclusions ? 'var(--color-warning)' : 'var(--color-text-light)' }}>
                                  {q.exclusions ? `⚑ ${q.exclusions.slice(0, 50)}${q.exclusions.length > 50 ? '...' : ''}` : 'None stated'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {memo.price_analysis && <p style={{ lineHeight: 1.8, color: 'var(--color-text-dark)' }}>{memo.price_analysis}</p>}
                </div>
              </div>
            )}

            {/* Reasoning */}
            {memo.reasoning && (
              <div className="card">
                <div className="card-header"><span style={{ fontWeight: 600 }}>Recommendation Reasoning</span></div>
                <div className="card-body">
                  <p style={{ lineHeight: 1.8, color: 'var(--color-text-dark)' }}>{memo.reasoning}</p>
                </div>
              </div>
            )}

            {/* Risk Assessment */}
            {memo.risk_assessment && (
              <div className="card">
                <div className="card-header"><span style={{ fontWeight: 600 }}>Risk Assessment</span></div>
                <div className="card-body">
                  <div className="risk-flag risk-flag-medium" style={{ marginBottom: 12 }}>
                    <span>⚑</span>
                    <p style={{ lineHeight: 1.8, margin: 0 }}>{memo.risk_assessment}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Scope Exclusions */}
            {memo.scope_exclusions && memo.scope_exclusions !== 'None identified' && (
              <div className="card">
                <div className="card-header">
                  <span style={{ fontWeight: 600 }}>Scope Exclusions</span>
                  <span className="badge badge-warning">Requires Attention</span>
                </div>
                <div className="card-body">
                  <div style={{ padding: '12px 16px', background: 'var(--color-warning-bg)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-warning)', fontSize: '0.875rem', lineHeight: 1.8, color: 'var(--color-text-dark)' }}>
                    {memo.scope_exclusions}
                  </div>
                </div>
              </div>
            )}

            {/* Human Decisions Required */}
            {memo.human_decisions_required?.length > 0 && (
              <div className="card">
                <div className="card-header">
                  <span style={{ fontWeight: 600 }}>Decisions Required from Procurement Team</span>
                  <span className="badge badge-error">{memo.human_decisions_required.length} decisions</span>
                </div>
                <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {memo.human_decisions_required.map((decision, i) => (
                    <div key={i} style={{ display: 'flex', gap: 12, padding: '12px 14px', background: 'var(--color-error-bg)', borderRadius: 'var(--radius-md)', border: '1px solid #FCA5A5' }}>
                      <span style={{ color: 'var(--color-error)', fontWeight: 700, flexShrink: 0 }}>{i + 1}.</span>
                      <span style={{ fontSize: '0.875rem', lineHeight: 1.6, color: 'var(--color-text-dark)' }}>{decision}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Conditions of Award */}
            {memo.conditions_of_award?.length > 0 && (
              <div className="card">
                <div className="card-header"><span style={{ fontWeight: 600 }}>Conditions of Award</span></div>
                <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {memo.conditions_of_award.map((condition, i) => (
                    <div key={i} style={{ display: 'flex', gap: 12, padding: '10px 14px', background: 'var(--color-background)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)' }}>
                      <span style={{ color: 'var(--color-teal)', fontWeight: 700, flexShrink: 0 }}>✓</span>
                      <span style={{ fontSize: '0.875rem', lineHeight: 1.6, color: 'var(--color-text-dark)' }}>{condition}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Final approval gate */}
            {!isApproved && (
              <div style={{ padding: '20px 24px', borderRadius: 'var(--radius-lg)', border: '2px solid var(--color-warning)', background: '#FFFBEB' }}>
                <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 8, color: 'var(--color-warning)' }}>
                  ⏳ Human Approval Required
                </div>
                <p style={{ fontSize: '0.875rem', lineHeight: 1.7, color: 'var(--color-text-dark)', marginBottom: 16 }}>
                  Review all sections of this memo carefully. Once you approve, the system will move this package to Contract/PO stage and record your decision in the audit trail. This action cannot be automatically reversed.
                </p>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={handleApprove} disabled={approving} className="btn btn-approve" style={{ padding: '12px 28px', fontSize: '1rem' }}>
                    {approving ? '...' : '✓ Approve Award'}
                  </button>
                  <button onClick={handleReject} className="btn btn-danger">
                    ✕ Reject — Return to Bid Leveling
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Right sidebar */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

            {/* Commercial summary */}
            <div className="card">
              <div className="card-header"><span style={{ fontWeight: 600 }}>Commercial Summary</span></div>
              <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {memo.commercial_summary ? (
                  <p style={{ fontSize: '0.875rem', lineHeight: 1.7, color: 'var(--color-text-dark)' }}>{memo.commercial_summary}</p>
                ) : (
                  <p style={{ color: 'var(--color-text-light)', fontSize: '0.875rem' }}>No commercial summary</p>
                )}
              </div>
            </div>

            {/* Next steps */}
            {memo.next_steps?.length > 0 && (
              <div className="card">
                <div className="card-header"><span style={{ fontWeight: 600 }}>Next Steps</span></div>
                <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {memo.next_steps.map((step, i) => (
                    <div key={i} style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: i < memo.next_steps.length - 1 ? '1px solid var(--color-border)' : 'none' }}>
                      <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'var(--color-teal-light)', color: 'var(--color-teal)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6875rem', fontWeight: 700, flexShrink: 0 }}>
                        {i + 1}
                      </div>
                      <span style={{ fontSize: '0.8125rem', lineHeight: 1.5, color: 'var(--color-text-dark)' }}>{step}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Package info */}
            <div className="card">
              <div className="card-header"><span style={{ fontWeight: 600 }}>Package Info</span></div>
              <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  ['Package', `PKG-${String(pkg.id).padStart(3, '0')}`],
                  ['Trade', pkg.trade_category],
                  ['State', state.replace(/_/g, ' ')],
                  ['Quotes Received', quotes.length],
                ].map(([label, value]) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8125rem' }}>
                    <span style={{ color: 'var(--color-text-light)' }}>{label}</span>
                    <span style={{ fontWeight: 500, color: 'var(--color-text-dark)' }}>{value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Audit trail */}
            <div className="card">
              <div className="card-header"><span style={{ fontWeight: 600 }}>Audit Trail</span></div>
              <div style={{ padding: '0 16px' }}>
                <AuditTrail entityType="package" entityId={packageId} maxItems={10} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MemoSection({ title, icon, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        {icon && <span style={{ color: 'var(--color-teal)', fontSize: '1rem' }}>{icon}</span>}
        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{title}</div>
      </div>
      <div style={{ fontSize: '0.9375rem', color: 'var(--color-text-dark)', lineHeight: 1.7 }}>{children}</div>
    </div>
  );
}